import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { AdfParseError } from '@atlassian/adf-parser/errors';
import { AdfParser } from '@atlassian/adf-parser/parser';
import { AdfRepairStrategy } from '@atlassian/adf-parser/repair-strategy';
import { AdfSchemaValidator } from '@atlassian/adf-parser/schema-validator';
import type { MultiFormatContentType } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';

import { log } from '../utils/aifc-logging';

/**
 * Chunk strategy for the HTML+ v2 incremental tool-call streaming contract.
 *
 * The HTML+ backend ALWAYS returns the tool-call value in
 * `application/adf+json` format (see the contract proposal under HTML+ Editor
 * Incremental Tool-Call Streaming Contract). Other content types are not
 * supported under v2; if one is observed we treat it as a contract violation,
 * fire an analytics event, and fall back to the ADF strategy so the snapshot
 * still parses (best-effort) rather than throwing mid-session.
 *
 * In v2 each chunk carries the **full snapshot** of the tool-call value (not a
 * delta appended to previous chunks). The strategy therefore MUST NOT
 * accumulate input across calls — it replaces internal state on every call.
 *
 * The corresponding v1 strategies in `tool-processor-chunk-strategies.ts`
 * remain unchanged; v1 callers continue to append per chunk and continue to
 * support all three content types.
 *
 * The v1 vs v2 decision is taken at the parser/processor layer (based on
 * whether the incoming chunk carries v2-shaped envelope metadata), so by the
 * time this factory is invoked that decision has already been made.
 */

const SUPPORTED_CONTENT_TYPE: MultiFormatContentType = 'application/adf+json';

export interface ToolProcessorChunkStrategyV2 {
	/** True when schema rejection removed every child of the parsed document. */
	getHadEmptySchemaContentRejection: () => boolean;
	/**
	 * Returns true if any ADF repair of type 'skip' was recorded during
	 * the most recent replaceSnapshot call, indicating schema content rejection
	 * (e.g. a blockCard placed inside a listItem).
	 */
	getHadSchemaContentRejection: () => boolean;
	getSchemaRejectionDetails: () => string | undefined;
	/**
	 * Replace the strategy's internal state with the full snapshot and return
	 * the corresponding ADF JSON string. Implementations MUST NOT append to
	 * previous calls.
	 */
	replaceSnapshot: (fullValue: string, isFinished: boolean) => string;
}

/**
 * ADF chunk strategy for v2. Each call instantiates a fresh `AdfParser` so the
 * parser's internal accumulator (`accumulatedInput += chunk`) never grows
 * across snapshots. The `AdfSchemaValidator` and `AdfRepairStrategy` are
 * hoisted onto the strategy instance and reused for every snapshot — those
 * are the only construction-time costs worth amortising.
 *
 * If profiling later shows the per-snapshot `new AdfParser(...)` cost is
 * measurable at real backend chunk rates, the `@atlassian/adf-parser`
 * package can grow a `processFullSnapshot(fullValue, isLastChunk)` method
 * and this strategy can switch to calling it without changing its public
 * surface. See `docs/html-plus-incremental-streaming-plan.md` §1f.
 */
class AdfChunkStrategyV2 implements ToolProcessorChunkStrategyV2 {
	private schemaValidator: AdfSchemaValidator | undefined;
	private repairStrategy: AdfRepairStrategy | undefined;
	private lastSerialisedDoc: string = '';
	private lastHadSchemaContentRejection: boolean = false;
	private lastHadEmptySchemaContentRejection: boolean = false;
	private lastSchemaRejectionDetails: string | undefined = undefined;

	constructor(schema?: Schema) {
		if (schema) {
			this.schemaValidator = new AdfSchemaValidator(schema);
			this.repairStrategy = new AdfRepairStrategy(schema);
		}
	}

	replaceSnapshot(fullValue: string, isFinished: boolean): string {
		if (!this.schemaValidator || !this.repairStrategy) {
			return fullValue;
		}

		const parser = new AdfParser({
			schemaValidator: this.schemaValidator,
			repairStrategy: this.repairStrategy,
		});

		try {
			parser.processChunk(fullValue, isFinished);
		} catch (error) {
			if (!(error instanceof AdfParseError)) {
				throw error;
			}
			// Tolerate partial / malformed snapshots — fall through to whatever
			// the parser was able to recover.
		}

		// Track whether any schema content was rejected during this snapshot.
		// Detect schema-invalidity drops: the AdfParser records a 'skip' repair whenever
		// a node is dropped because it cannot fit in its parent context. Among all skip
		// repair patterns in the parser, the schema-invalidity path (parser.ts line ~361)
		// is uniquely identified by:
		//   - to === '-'            : node dropped, not moved/inferred elsewhere
		//   - context has no ':'    : excludes 'empty_after_lift' ('listItem:empty_after_lift')
		//                            and 'empty_value' ('parent.key:empty_value')
		//   - context !== 'parser'  : excludes parser-token errors (orphaned braces, EOF, etc.)
		// All other non-schema skip patterns either use to !== '-' (inferred delimiters, arrays)
		// or include ':' in their context string. This filter is therefore both necessary
		// and sufficient to identify genuine ADF schema content rejection.
		const schemaRejections = parser.getRepairs().filter(
			(r) =>
				r.type === 'skip' &&
				r.to === '-' &&
				!r.context.includes(':') &&
				r.context !== 'parser' &&
				// Exclude orphaned_closing_brace — a parser structural artifact, not a schema error.
				// Its context is parentType || 'content' so it passes the other checks.
				r.from !== 'orphaned_closing_brace',
		);
		this.lastHadSchemaContentRejection = schemaRejections.length > 0;
		this.lastSchemaRejectionDetails =
			schemaRejections.length > 0
				? schemaRejections.map((r) => `${r.from} in ${r.context}`).join(', ')
				: undefined;

		// `getPartialDocument()` may return `null` when the parser has not
		// yet recovered any usable partial document (e.g. the very first
		// chunk was malformed). `JSON.stringify(null)` returns the literal
		// string `'null'` (not `undefined`), so a naïve
		// `JSON.stringify(parser.getPartialDocument()) ?? this.lastSerialisedDoc`
		// would ship `'null'` to the orchestrator, which would then try to
		// parse it as an ADF body — wiping the user's document. Guard the
		// `null`/`undefined` case explicitly and keep the previous snapshot.
		const partial = parser.getPartialDocument();
		this.lastHadEmptySchemaContentRejection =
			this.lastHadSchemaContentRejection && partial?.type === 'doc' && !partial.content?.length;
		if (partial !== null && partial !== undefined) {
			this.lastSerialisedDoc = JSON.stringify(partial);
		}
		return this.lastSerialisedDoc;
	}

	getHadSchemaContentRejection(): boolean {
		return this.lastHadSchemaContentRejection;
	}

	getHadEmptySchemaContentRejection(): boolean {
		return this.lastHadEmptySchemaContentRejection;
	}

	getSchemaRejectionDetails(): string | undefined {
		return this.lastSchemaRejectionDetails;
	}
}

/**
 * Build a v2 chunk strategy. HTML+ streaming under v2 ALWAYS uses
 * `application/adf+json`; any other contentType is a contract violation. We
 * fire `htmleditor.contract_violation.v2_unexpected_content_type` and then
 * fall back to the ADF strategy on best-effort grounds (callers should treat
 * this as a bug to be fixed upstream rather than rely on the fallback).
 */
export const createToolProcessorChunkStrategyV2 = (
	contentType: MultiFormatContentType | undefined,
	schema?: Schema,
): ToolProcessorChunkStrategyV2 => {
	if (contentType !== undefined && contentType !== SUPPORTED_CONTENT_TYPE) {
		log('htmleditor.contract_violation.v2_unexpected_content_type', {
			streamingContract: 'v2',
			expectedContentType: SUPPORTED_CONTENT_TYPE,
			receivedContentType: contentType,
		});
	}

	return new AdfChunkStrategyV2(schema);
};
